import {lazy, Suspense, useEffect} from "react";
import SrollTop from "./components/common/ScrollTop";
import "swiper/css";
import "swiper/css/navigation";
import "./styles/index.scss";
import {Provider} from "react-redux";
import {store} from "./store/store";
import {BrowserRouter, Route, Routes} from "react-router-dom";
import ScrollTopBehaviour from "./components/common/ScrollTopBehaviour";
import Home from "./pages";
import Footer8 from "@/components/footer/footer-8/index.jsx";
import "@/styles/cookieconsent.css";
import * as CookieConsent from "vanilla-cookieconsent";

// Everything but the homepage is lazy-loaded: App.jsx used to import every route's page
// component (and everything they pull in - leaflet, photoswipe, chart.js, etc.) eagerly,
// so all of it landed in the one bundle every visitor downloaded before seeing the
// homepage, regardless of which page they were on. React.lazy + Suspense lets Rollup split
// each route into its own chunk that only loads when that route is actually visited.
const NotFoundPage = lazy(() => import("./pages/not-found"));
const TourListPage1 = lazy(() => import("./pages/tour/tour-list-v1"));
const TourListPage2 = lazy(() => import("./pages/tour/tour-list-v2"));
const TourSingleV1Dynamic = lazy(() => import("./pages/tour/tour-single"));
const Contact = lazy(() => import("./pages/others/contact"));
const Terms = lazy(() => import("@/pages/others/terms/index.jsx"));
const BestOffers = lazy(() => import("@/pages/best-offers/index.jsx"));
const Flights = lazy(() => import("@/pages/flights/index.jsx"));
const BestFlights = lazy(() => import("@/pages/best-flights/index.jsx"));
const BecomeExpert = lazy(() => import("@/pages/others/become-expert/index.jsx"));
const DestinationMap = lazy(() => import("@/pages/map/index.jsx"));
const DestinationPage = lazy(() => import("@/pages/destination/index.jsx"));
const BlogListPage = lazy(() => import("@/pages/blog/index.jsx"));
const BlogPostPage = lazy(() => import("@/pages/blog/post.jsx"));
const TripCreatorPage = lazy(() => import("@/pages/trip-creator/index.jsx"));
const SeoCategoryPage = lazy(() => import("@/pages/seo-category/index.jsx"));
const MethodologyPage = lazy(() => import("@/pages/methodology/index.jsx"));
const SeoStaticPage = lazy(() => import("@/pages/seo-static/index.jsx"));

if (typeof window !== "undefined") {
  import("bootstrap");
}

function App() {
  useEffect(() => {
    CookieConsent.run({

      categories: {
        necessary: {
          enabled: true,  // this category is enabled by default
          readOnly: true  // this category cannot be disabled
        },
        analytics: {
          autoClear: {
            cookies: [
              {
                name: /^_ga/,   // regex: match all cookies starting with '_ga'
              },
              {
                name: '_gid',   // string: exact cookie name
              }
            ]
          }
        },
      },

      language: {
        default: 'en',
        translations: {
          en: {
            consentModal: {
              title: 'Używamy ciasteczek',
              description: 'aby dostarczyć Ci jeszcze lepsze doświadczenie',
              acceptAllBtn: 'Zaakceptuj wszystkie',
              acceptNecessaryBtn: 'Odrzuć wszystkie',
              showPreferencesBtn: 'Zarządzaj ustawieniami'
            },
            preferencesModal: {
              title: 'Zarządzaj ustawieniami',
              acceptAllBtn: 'Zaakceptuj wszystkie',
              acceptNecessaryBtn: 'Odrzuć wszystkie',
              savePreferencesBtn: 'Zaakceptuj obecny wybór',
              closeIconLabel: 'Zamknij',
              sections: [
                {
                  title: 'Czy ktoś powiedział ... ciasteczka?',
                  description: 'Używamy plików cookie aby lepiej zrozumieć Twoje doświadczenie i dostarczać jeszcze lepsze ' +
                      'oferty na tanie podróżowanie. W każdej chwili możesz zmienić swoje ustawienia.',
                },
                {
                  title: 'Potrzebne do działania strony',
                  description: 'Te ciasteczka są niezbędne do działania strony i nie mogą być wyłączone w naszych systemach',
                  linkedCategory: 'necessary'
                },
                {
                  title: 'Wydajność, analityka i reklamy',
                  description: 'Te ciasteczka pozwalają nam m.in. liczyć wizyty i ruch, abyśmy mogli lepiej zrozumieć czego u nas szukasz.',
                  linkedCategory: 'analytics'
                },
                {
                  title: 'Więcej informacji',
                  description: 'Szczegółowe informacje dotyczące polityki cookies są dostępne <a href="/terms">tutaj</a>'
                }
              ]
            }
          }
        }
      }
    });
  }, []);

  return (
    <div id="app-shell">
      <Provider store={store}>
        <BrowserRouter>
          <Suspense fallback={<div style={{minHeight: '100vh'}} />}>
          <Routes>
            <Route path="/">
              <Route index element={<Home />} />
              <Route path="404" element={<NotFoundPage />} />
              <Route path="*" element={<NotFoundPage />} />

              {/*<Route path="about" element={<About />} />*/}
              <Route path="summary" element={<BecomeExpert />} />
              {/*<Route path="help-center" element={<HelpCenter />} />*/}
              {/*<Route path="signup" element={<SignUp />} />*/}
              {/*<Route path="login" element={<LogIn />} />*/}
              <Route path="terms" element={<Terms />} />
              {/*<Route path="invoice" element={<Invoice />} />*/}
              <Route path="contact" element={<Contact />} />
              <Route path="best-offers" element={<BestOffers/>} />
              <Route path="flights" element={<Flights/>} />
              <Route path="best-flights" element={<BestFlights/>} />
              <Route path="kreator-podrozy" element={<TripCreatorPage/>} />
              <Route path="last-minute" element={<SeoCategoryPage/>} />
              <Route path="all-inclusive" element={<SeoCategoryPage/>} />
              <Route path="wakacje-z-dziecmi" element={<SeoCategoryPage/>} />
              <Route path="city-break" element={<SeoCategoryPage/>} />
              <Route path="metodologia" element={<MethodologyPage/>} />
              <Route path="z-warszawy" element={<SeoStaticPage/>} />
              <Route path="z-krakowa" element={<SeoStaticPage/>} />
              <Route path="z-gdanska" element={<SeoStaticPage/>} />
              <Route path="z-wroclawia" element={<SeoStaticPage/>} />
              <Route path="z-katowic" element={<SeoStaticPage/>} />
              <Route path="wakacje-w-maju" element={<SeoStaticPage/>} />
              <Route path="wakacje-we-wrzesniu" element={<SeoStaticPage/>} />
              <Route path="ferie-zimowe" element={<SeoStaticPage/>} />
              <Route path="weekend-majowy" element={<SeoStaticPage/>} />
              <Route path="o-nas" element={<SeoStaticPage/>} />
              <Route path="zasady-redakcyjne" element={<SeoStaticPage/>} />
              {/*<Route path="destinations" element={<Destinations />} />*/}

              {/*<Route path="dashboard">*/}
              {/*  <Route path="db-dashboard" element={<DBDashboard />} />*/}
              {/*  <Route path="db-booking" element={<DBBooking />} />*/}
              {/*  <Route path="db-wishlist" element={<DBWishlist />} />*/}
              {/*  <Route path="db-settings" element={<DBSettings />} />*/}
              {/*</Route>*/}

              {/*<Route path="vendor-dashboard">*/}
              {/*  <Route path="dashboard" element={<VendorDashboard />} />*/}
              {/*  <Route path="add-hotel" element={<VendorAddHotel />} />*/}
              {/*  <Route path="booking" element={<VendorBooking />} />*/}
              {/*  <Route path="hotels" element={<BVVendorHotel />} />*/}
              {/*  <Route path="recovery" element={<BDVendorRecovery />} />*/}
              {/*</Route>*/}

              {/*<Route path="hotel-list-v1" element={<HotelListPage1 />} />*/}
              {/*<Route path="hotel-list-v2" element={<HotelListPage2 />} />*/}
              {/*<Route path="hotel-list-v3" element={<HotelListPage3 />} />*/}
              {/*<Route path="hotel-list-v4" element={<HotelListPage4 />} />*/}
              {/*<Route path="hotel-list-v5" element={<HotelListPage5 />} />*/}
              {/*<Route*/}
              {/*  path="hotel-single-v1/:id"*/}
              {/*  element={<HotelSingleV1Dynamic />}*/}
              {/*/>*/}
              {/*<Route*/}
              {/*  path="hotel-single-v2/:id"*/}
              {/*  element={<HotelSingleV2Dynamic />}*/}
              {/*/>*/}
              {/*<Route path="booking-page" element={<BookingPage />} />*/}

              <Route path="tour-list-v1" element={<TourListPage1 />} />
              <Route path="tour-list-v2" element={<TourListPage2 />} />
              {/*<Route path="tour-list-v3" element={<TourListPage3 />} />*/}
              <Route path="tour-single/:id" element={<TourSingleV1Dynamic />} />

              {/*<Route path="activity-list-v1" element={<ActivityListPage1 />} />*/}
              {/*<Route path="activity-list-v2" element={<ActivityListPage2 />} />*/}
              {/*<Route path="activity-list-v3" element={<ActivityListPage3 />} />*/}
              {/*<Route*/}
              {/*  path="activity-single/:id"*/}
              {/*  element={<ActivitySingleV1Dynamic />}*/}
              {/*/>*/}

              {/*<Route path="rental-list-v1" element={<RentalListPage1 />} />*/}
              {/*<Route path="rental-list-v2" element={<RentalListPage2 />} />*/}
              {/*<Route path="rental-list-v3" element={<RentalListPage3 />} />*/}
              {/*<Route*/}
              {/*  path="rental-single/:id"*/}
              {/*  element={<RentalSingleV1Dynamic />}*/}
              {/*/>*/}

              {/*<Route path="car-list-v1" element={<CarListPage1 />} />*/}
              {/*<Route path="car-list-v2" element={<CarListPage2 />} />*/}
              {/*<Route path="car-list-v3" element={<CarListPage3 />} />*/}
              {/*<Route path="car-single/:id" element={<CarSingleV1Dynamic />} />*/}

              {/*<Route path="cruise-list-v1" element={<CruiseListPage1 />} />*/}
              {/*<Route path="cruise-list-v2" element={<CruiseListPage2 />} />*/}
              {/*<Route path="cruise-list-v3" element={<CruiseListPage3 />} />*/}
              {/*<Route*/}
              {/*  path="cruise-single/:id"*/}
              {/*  element={<CruiseSingleV1Dynamic />}*/}
              {/*/>*/}

              {/*<Route path="flight-list-v1" element={<FlightListPage1 />} />*/}
              <Route path="map" element={<DestinationMap/>} />

              {/* Programmatic SEO destination hubs - one per curated destination, route list
                  derived at request time from /api/trips/destinations/all (see
                  pages/destination/index.jsx), not hardcoded here. */}
              <Route path="kierunek/:destinationSlug" element={<DestinationPage/>} />

              <Route path="blog" element={<BlogListPage/>} />
              <Route path="blog/:slug" element={<BlogPostPage/>} />
            </Route>
          </Routes>
          </Suspense>
          <ScrollTopBehaviour />
          <Footer8/>
        </BrowserRouter>

        <SrollTop />
      </Provider>
      {/*<CookieConsent*/}
      {/*    location="bottom"*/}
      {/*    buttonText="Zaakceptuj"*/}
      {/*    cookieName="cookie_consent"*/}
      {/*    style={{ background: "#0d2857" }}*/}
      {/*    buttonStyle={{ color: "#4e503b", fontSize: "13px" }}*/}
      {/*    expires={150}*/}
      {/*>*/}
      {/*  Korzystamy z ciasteczek, aby zapewnić najlepsze doświadczenia na naszej stronie.*/}
      {/*  <a*/}
      {/*  href="/terms"> <u>Polityka cookies</u>*/}
      {/*  </a>*/}
      {/*</CookieConsent>*/}
    </div>
  );
}

export default App;
